import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Clock3, Crown, LoaderCircle, LockKeyhole, ShieldCheck, Trophy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignEntry } from '@/contexts/campaignEntryContext';
import { useCampaignEntryCopy } from '@/i18n/campaignEntry';
import { getHomeRouteForObjective } from '@/hooks/use-home-route';
import { SUPER_HERO_BANNER_URL, SUPER_HERO_CAMPAIGN_ID, SUPER_HERO_CAMPAIGN_PATH } from '@/lib/campaigns/superHeroCampaign';
import { CampaignArtwork } from '@/components/campaign-entry/CampaignArtwork';
import { SuperHeroAuthForm } from '@/components/campaign-entry/SuperHeroAuthForm';
import '@/components/campaign-entry/superHeroEntry.css';

type EntryFailure = 'unavailable' | 'failed' | 'stale';

function isUnavailable(cause: unknown): boolean {
  if (cause instanceof Error && cause.message === 'campaign_entry_unavailable') return true;
  if (!cause || typeof cause !== 'object') return false;
  const error = cause as { code?: string; extensions?: { code?: string }; graphQLErrors?: { message?: string; extensions?: { code?: string } }[] };
  return error.code === 'campaign_entry_unavailable' || error.extensions?.code === 'campaign_entry_unavailable'
    || Boolean(error.graphQLErrors?.some(item => item.message === 'campaign_entry_unavailable' || item.extensions?.code === 'campaign_entry_unavailable'));
}

export function SuperHeroEntry() {
  const copy = useCampaignEntryCopy();
  const { user, isLoading, sessionVerified, recheckConnection } = useAuth();
  const { completeEntry } = useCampaignEntry();
  const navigate = useNavigate();
  const [retry, setRetry] = useState(0);
  const [failure, setFailure] = useState<EntryFailure | null>(null);
  const sessionKey = user ? String(user.id) : null;
  const currentSession = useRef(sessionKey);
  currentSession.current = sessionKey;
  const request = useRef<{ key: string; promise: Promise<boolean> } | null>(null);

  useEffect(() => {
    if (!sessionKey || isLoading || !sessionVerified) {
      request.current = null;
      setFailure(null);
      return;
    }
    const attemptKey = `${sessionKey}:${retry}`;
    if (request.current?.key !== attemptKey) {
      setFailure(null);
      request.current = {
        key: attemptKey,
        promise: Promise.resolve().then(() => currentSession.current === sessionKey ? completeEntry(SUPER_HERO_CAMPAIGN_ID) : false),
      };
    }
    const attempt = request.current;
    let active = true;
    // StrictMode reattaches to the same attempt; it never submits twice.
    attempt.promise.then(completed => {
      if (!active || currentSession.current !== sessionKey || request.current !== attempt) return;
      if (completed) navigate(SUPER_HERO_CAMPAIGN_PATH, { replace: true });
      else setFailure('stale');
    }).catch(cause => {
      if (active && currentSession.current === sessionKey && request.current === attempt) setFailure(isUnavailable(cause) ? 'unavailable' : 'failed');
    });
    return () => { active = false; };
  }, [sessionKey, isLoading, sessionVerified, retry, completeEntry, navigate]);

  const sessionUnverified = Boolean(user && !isLoading && !sessionVerified);
  const waiting = isLoading || Boolean(user && !failure && !sessionUnverified);

  return <main className="super-hero-entry" data-testid="super-hero-entry">
    <div className="super-hero-page">
      <header className="super-hero-header">
        <Link to="/" className="super-hero-brand" aria-label="Autoclipper"><img src="/images/autoclipper-logo-full.png" alt="Autoclipper" width="156" height="36" /></Link>
        <span className="super-hero-preview"><Trophy aria-hidden="true" />{copy.campaign}</span>
      </header>

      <div className="super-hero-content">
        <section className="super-hero-poster" aria-labelledby="super-hero-title">
          <div className="super-hero-masthead">
            <p className="super-hero-eyebrow"><span />EL GATO <span className="super-hero-multiply">×</span> {copy.campaignTag}</p>
            <h1 id="super-hero-title">{copy.headline}<Crown aria-hidden="true" /></h1>
            <p className="super-hero-tagline">{copy.tagline}</p>
          </div>
          <div className="super-hero-poster-frame">
            <CampaignArtwork src={SUPER_HERO_BANNER_URL} alt={copy.artworkAlt} className="super-hero-poster-art" />
            <span className="super-hero-poster-corner" aria-hidden="true" />
          </div>
          <div className="super-hero-prizes">
            <div className="super-hero-prize"><Trophy aria-hidden="true" /><div><strong>{copy.prize}</strong><span>{copy.prizeLabel}</span></div></div>
            <div className="super-hero-places"><strong>{copy.places}</strong><span>{copy.placesLabel}</span></div>
          </div>
          <div className="super-hero-rules"><ShieldCheck aria-hidden="true" /><p><strong>{copy.payment}</strong> {copy.rules}</p></div>
        </section>

        <section className="super-hero-account" aria-label={copy.accountStep}>
          <div className="super-hero-account-body">
            {!user && !isLoading ? <SuperHeroAuthForm /> : <div className="super-hero-entry-state" aria-busy={waiting}>
              <span className="super-hero-eyebrow">{user ? copy.signedIn : copy.accountStep}</span>
              {user && <p className="super-hero-current-account">{user.email}</p>}
              <div className={`super-hero-state-icon ${failure || sessionUnverified ? 'is-unavailable' : ''}`}>
                {waiting ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : failure === 'unavailable' ? <Clock3 aria-hidden="true" /> : <LockKeyhole aria-hidden="true" />}
              </div>
              <h2 className="super-hero-form-title">{waiting ? (isLoading ? copy.checkingSession : copy.completing) : failure === 'unavailable' ? copy.unavailableTitle : copy.entryFailedTitle}</h2>
              {failure || sessionUnverified ? <>
                <p role="alert" className="super-hero-entry-error">{sessionUnverified ? copy.sessionFailed : failure === 'unavailable' ? copy.unavailable : failure === 'stale' ? copy.staleSession : copy.entryFailed}</p>
                <Button className="super-hero-primary" onClick={() => { if (sessionUnverified) void recheckConnection?.(); else setRetry(previous => previous + 1); }}>{copy.retry}<ArrowRight aria-hidden="true" /></Button>
                <Link to={getHomeRouteForObjective(user?.onboarding_objective)} className="super-hero-account-link">{copy.account}<ArrowRight aria-hidden="true" /></Link>
              </> : <p className="super-hero-form-description" role="status">{copy.completingDetail}</p>}
            </div>}
          </div>
          <div className="super-hero-preview-notice"><ShieldCheck aria-hidden="true" /><p>{copy.entryNotice}</p></div>
          <div className="super-hero-account-footer"><ShieldCheck aria-hidden="true" />{copy.poweredBy}</div>
        </section>
      </div>

      <footer className="super-hero-footer"><span>EL GATO × SUPER HERO</span><p>{copy.footer}</p></footer>
    </div>
  </main>;
}

export default SuperHeroEntry;
